import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth.js';
import { withOfflineFallback } from '../utils/offline.js';
import { db, type Cliente } from '../db/dexie.js';
import api from '../services/auth.service.js';

export const CLIENTES_QUERY_KEY = 'clientes';

// AIDEV-NOTE: Interface alinhada com os campos acessados em ClienteTable.tsx (snake_case do Supabase)
export interface ClienteRow {
  id?: string;
  codigo: number;
  razao_social: string;
  nome_fantasia?: string;
  cnpj_cpf: string;
  cidade: string;
  uf: string;
  telefone?: string;
  email?: string;
  situacao: string;
  limite_credito?: number;
  vendedor_id?: number;
  latitude?: number;
  longitude?: number;
}

interface UseClientesOptions {
  busca?: string;
  situacao?: 'A' | 'I';
  cidade?: string;
}

// AIDEV-NOTE: Mapeia campos camelCase do Dexie para snake_case esperado pelos componentes.
// Aceita ambos os formatos para resiliência (dados podem vir de qualquer fonte).
function mapDexieClienteToRow(c: Cliente): ClienteRow {
  return {
    codigo: c.codigo,
    razao_social: c.razaoSocial ?? '',
    nome_fantasia: c.nomeFantasia,
    cnpj_cpf: c.cnpjCpf ?? '',
    cidade: c.cidade ?? '',
    uf: c.uf ?? '',
    telefone: c.telefone,
    email: c.email,
    situacao: c.situacao ?? 'A',
    limite_credito: c.limite_credito,
    vendedor_id: c.vendedor_id,
    latitude: c.latitude,
    longitude: c.longitude
  };
}

// AIDEV-NOTE: Hook para listagem de clientes com suporte a filtros e fallback offline via Dexie.
// Usa REST API (/clientes) em vez de Supabase direto — app migrado para Postgres local.
export function useClientes(opts: UseClientesOptions = {}) {
  const { user } = useAuth();

  return useQuery({
    queryKey: [CLIENTES_QUERY_KEY, user?.client_id, opts],
    enabled: !!user,
    // AIDEV-PERF: 5 min staleTime — clientes mudam com pouca frequência e a lista pode ser grande
    staleTime: 5 * 60 * 1000,
    queryFn: (): Promise<ClienteRow[]> =>
      withOfflineFallback(
        async () => {
          const params: Record<string, string | number> = { limit: 500 };
          if (opts.busca) params.q = opts.busca;
          if (opts.situacao) params.situacao = opts.situacao;
          if (opts.cidade) params.cidade = opts.cidade;

          const res = await api.get<{ clientes: ClienteRow[] }>('/clientes', { params });
          return res.data.clientes;
        },
        // AIDEV-NOTE: Dexie usa camelCase, componentes esperam snake_case — mapDexieClienteToRow faz a conversão
        async () => {
          const results = await db.clientes.orderBy('razaoSocial').toArray();
          return results.map(mapDexieClienteToRow);
        }
      )
  });
}

/**
 * Hook para verificar se o usuário pode cadastrar novos clientes
 * AIDEV-NOTE: Controlado pela política pode_cadastrar_clientes no JWT
 */
export function usePodeCadastrarClientes(): boolean {
  const { user } = useAuth();
  return user?.pode_cadastrar_clientes ?? false;
}
